import AddIcon from '@mui/icons-material/Add';
import {
  Row, Col, Tag, Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from 'ahooks';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyButton from '../../../components/AlumlyButton';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import { PathBuilder } from '../../../utils/pathBuilder';
import FormFilterEventList, { FilterEventT } from './FormFilterEventList';

const categoryColors: Record<string, string> = {
  seminar: 'green',
  social: 'gold',
  sport: 'blue',
  charity: 'red',
};

const EventList: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermission();

  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterEventT>();
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    rowCount: 0,
    loading: false,
  });

  const debouncedFilter = useDebounce(selectedFilter, { wait: 300 });
  const { service: getEventService } = useServices(ServiceNameEnum.event.index);

  const getEvents = async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await getEventService.get({});
    if (data) {
      setEvents(data);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
    setLoading(false);
  };

  const filteredEvents = useMemo(() => {
    let result = events;
    if (debouncedFilter?.searchQuery) {
      const q = debouncedFilter.searchQuery.toLowerCase();
      result = result.filter((e) =>
        (e.titleML?.en || '').toLowerCase().includes(q)
        || (e.titleML?.th || '').toLowerCase().includes(q));
    }
    if (debouncedFilter?.category) {
      result = result.filter((e) => e.category === debouncedFilter.category);
    }
    if (debouncedFilter?.status !== undefined && debouncedFilter?.status !== '') {
      const isPublished = debouncedFilter.status === 'true';
      result = result.filter((e) => e.isPublished === isPublished);
    }
    return result;
  }, [events, debouncedFilter]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      rowCount: filteredEvents.length,
      page: 0,
    }));
  }, [filteredEvents]);

  const onRowClick = (rowData: any): void => {
    const params = { id: rowData.id };
    const detailPath = PathBuilder.buildPath(RouteNameEnum.event.detail, { params });
    navigate(detailPath);
  };

  const columns: any[] = [
    {
      field: 'image',
      headerName: 'Image',
      width: 120,
      renderCell: (params: any) => (
        params.row.coverImageUrl
          ? <img src={params.row.coverImageUrl} alt="" style={{ width: 80, height: 60, borderRadius: 4, objectFit: 'cover' }} />
          : <div style={{ width: 80, height: 60, borderRadius: 4, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography.Text type="secondary" style={{ fontSize: 12 }}>No image</Typography.Text></div>
      ),
    },
    {
      field: 'titleML',
      headerName: 'Title',
      width: 250,
      flex: 1,
      renderCell: (params: any) => (
        <Typography.Text style={{ fontSize: 14, wordWrap: 'break-word', whiteSpace: 'normal' }}>
          {params.row.titleML?.en || params.row.titleML?.th || '-'}
        </Typography.Text>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      renderCell: (params: any) => (
        params.row.category ? (
          <Tag
            color={categoryColors[params.row.category] || 'default'}
          >
            {params.row.category}
          </Tag>
        ) : <Typography.Text>-</Typography.Text>
      ),
    },
    {
      field: 'startAt',
      headerName: 'Event Date',
      width: 200,
      renderCell: (params: any) => (
        <div style={{
          display: 'flex-start', alignItems: 'center', flexDirection: 'column',
        }}
        >
          <Typography.Text style={{ fontSize: 14 }}>
            {DateTimeUtils.dateTimeFormat(params.row.startAt)}
          </Typography.Text>
          {params.row.endAt && (
            <Typography.Text style={{ fontSize: 14 }}>
              {`- ${DateTimeUtils.dateTimeFormat(params.row.endAt)}`}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      field: 'registrationCount',
      headerName: 'Registrations',
      width: 140,
      renderCell: (params: any) => {
        const { registrationCount, maxParticipants } = params.row;
        const count = registrationCount ?? 0;
        if (maxParticipants != null) {
          const isFull = count >= maxParticipants;
          return (
            <div>
              <Typography.Text style={{ fontSize: 14, fontWeight: 'bold', color: isFull ? '#ff4d4f' : undefined }}>
                {`${count} / ${maxParticipants}`}
              </Typography.Text>
              {isFull && (
                <Tag color="red" style={{ height: 20, fontSize: 11 }}>Full</Tag>
              )}
            </div>
          );
        }
        return (
          <Typography.Text style={{ fontSize: 14 }}>
            {count > 0 ? count : '-'}
          </Typography.Text>
        );
      },
    },
    {
      field: 'isPublished',
      headerName: 'Status',
      width: 120,
      renderCell: (params: any) => (
        <div>
          <StatusBadge status={params.row.isPublished} />
        </div>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      valueGetter: (params: any) => DateTimeUtils.dateTimeFormat(params.row.createdAt),
    },
  ];

  useEffect(() => {
    getEvents();
  }, []);

  return (
    <div style={{ marginTop: 32 }}>
      <Row>
        {checkPermission([PermissionEnum.code.eventCreate])
          && (
            <Col xs={24} style={{ textAlign: 'end' }}>
              <AlumlyButton
                icon={<AddIcon />}
                label="rxv"
                width={240}
                onClick={() => navigate(RouteNameEnum.event.create)}
              >
                Create new event
              </AlumlyButton>
            </Col>
          )}
      </Row>
      <FormFilterEventList
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 32,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex' }}>
            <Typography.Text style={{ marginRight: 8 }}>Total</Typography.Text>
            <Typography.Text strong>{`${filteredEvents.length} Items`}</Typography.Text>
          </div>
        </div>
      </div>
      <AlumlyDataGrid
        rowHeight={80}
        height={700}
        rows={filteredEvents}
        columns={columns}
        onRowClick={
          checkPermission([PermissionEnum.code.eventEdit])
            ? onRowClick
            : undefined
        }
        {...pagination}
        loading={loading}
        onPageChange={(page: number) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default EventList;
